import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Socket } from "socket.io-client";
import { getComponentsImport, getSetupImport } from "src/components/gametable/projectImports";
import { mergeDeep } from "src/components/gametable/tableUtility";
import { UsernameDisplay } from "src/components/pages/UserPage";
import { getCgoColor } from "src/components/playerColors";
import { DataSheet, DeckInfo, PlayerColorInfo, TableInfo, TableOptions } from "src/components/types";
import gameSituationSetuper from "./gametable/GameSituationSetuper";
import "./TableTile.css";
import { Delete, Link } from "@mui/icons-material";
import { Alert, CircularProgress, Fade, Tooltip } from "@mui/material";
import { set } from "lodash";

let setup: any, components: any;
async function startTable(
  event: React.MouseEvent,
  tableInfo: TableInfo,
  deckData: { [key: string]: DeckInfo },
  defaultOptions: TableOptions,
  projectSlug: string,
  components: { [key: string]: any },
  dataSheet: DataSheet,
) {
  event.preventDefault();
  let completeOptions = Object.assign(defaultOptions, tableInfo.options);
  for (let option in completeOptions) {
    completeOptions[option] = completeOptions[option].value;
  }
  completeOptions.selectedColors = tableInfo.playerColors.map((p) => p.color);
  completeOptions.numPlayers = completeOptions.selectedColors.length;

  let setupPromise;
  let componentsPromise;
  let changeLogPromise;
  let allPromises = [];
  if (!setup) {
    setupPromise = getSetupImport(projectSlug)
      .then((module) => {
        setup = module.default;
      })
      .catch((err) => {
        console.log(err);
        return;
      });
    allPromises.push(setupPromise);
  }
  componentsPromise = getComponentsImport(projectSlug)
    .then((module) => {
      components = module.default;
      let projectData = tableInfo.projectData ?? tableInfo.projectInfo; // TODO: find out why sometimes it is projectData and sometimes projectInfo
      mergeDeep(module.default, projectData.componentPool ?? {});
      window.componentPool = module.default;
    })
    .catch((err) => {
      console.log(err);
      throw new Error("Could not load components: " + err.message);
    });
  allPromises.push(componentsPromise);
  await Promise.all(allPromises);
  await finishCreateTable();

  async function finishCreateTable() {
    let situation = gameSituationSetuper.createNewSituation(
      projectSlug,
      components,
      completeOptions,
      deckData,
      dataSheet,
    );
    if (!setup) {
      throw new Error("Setup function didn't load correctly. Try to refresh the page.");
    }
    await setup(situation);
    let response = await fetch("/api/projects/" + projectSlug + "/" + tableInfo.tableId + "/start-table", {
      method: "POST",
      body: JSON.stringify({
        start_position: situation.getPositionData(),
        components,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });
    let tableData;
    if (response.ok) {
      tableData = await response.json();
    } else {
      throw response;
    }
    console.log(tableData);
  }
}

type TableTileProps = {
  projectName?: string;
  dataSheet?: DataSheet;
  tableInfo: TableInfo;
  socket?: Socket;
  tableStarted?: Function;
  projectSlug?: string;
  decks?: { [key: string]: DeckInfo };
  activePlayer?: string;
  options: { [key: string]: any };
  globalAssets?: { [key: string]: any };
  showAllOptions?: boolean;
};

const newUserAudio = new Audio("/assets/sounds/knockknock.mp3");

function TableTile(props: TableTileProps) {
  let optionsDisplay = [];
  let tableOptions = props.tableInfo.options ?? props.tableInfo?.start_position?.options ?? {};
  const [tableInfo, setTableInfo] = useState(props.tableInfo);
  const [errorMessage, setErrorMessage] = useState("");
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [deleteProgressVisible, setDeleteProgressVisible] = useState<boolean>(false);
  const [joinLeaveProgressColor, setJoinLeaveProgressColor] = useState<string>("");

  if (!tableInfo) {
    return <div>No table info</div>;
  }
  const projectSlug = props.tableInfo.projectInfo?.slug;
  const playerName = localStorage.getItem("user-name");
  const tableUrl = "/projects/" + projectSlug + "/table/" + tableInfo.tableId;

  let sortedOptions = Object.keys(tableOptions ?? {}).toSorted((a, b) => {
    return (props.options[a].displayOrder ?? 0) - (props.options[b].displayOrder ?? 0);
  });

  let navigate = useNavigate();
  let params = useParams();
  let defaultOptions: TableOptions = {};

  const tableStarted = useCallback(
    (tableId: string) => {
      if (tableInfo.tableId === tableId) {
        const userIsInTable = tableInfo.playerColors.filter(
          (c) => c.userInfo?.username === localStorage.getItem("user-name"),
        )[0];
        if (userIsInTable) {
          let tableStartNotification =
            Notification &&
            new Notification("Your CGO table " + tableId + " just started, click here to go to the table");

          if (tableStartNotification) {
            tableStartNotification.onclick = function () {
              window.focus();
              navigate(tableUrl);
              this.close();
            };
          }
          if (props.tableStarted) {
            props.tableStarted();
          }

          if (userIsInTable) {
            navigate(tableUrl);
            let tabAnimation = [
              "▲Table starting",
              "▶Table starting",
              "▼Table starting",
              "◀Table starting",
              "▲Table starting",
              "▶Table starting",
              "▼Table starting",
              "◀Table starting",
              "▲Table starting",
              "▶Table starting",
              "▼Table starting",
              "◀Table starting",
              document.title,
            ];
            for (let i in tabAnimation) {
              window.setTimeout(() => {
                document.title = tabAnimation[i];
              }, +i * 500);
            }
          }
        }
      }
    },
    [navigate, props],
  );

  useEffect(() => {
    if (props.socket) {
      props.socket.on("player-joined", playerJoined);
      props.socket.on("player-left", playerLeft);
      props.socket.on("remove-table", tableRemoved);
      props.socket.on("table-started", tableStarted);
    }
    return () => {
      props.socket?.off("player-joined", playerJoined);
      props.socket?.off("player-left", playerLeft);
      props.socket?.off("remove-table", tableRemoved);
      props.socket?.off("table-started", tableStarted);
    };
  }, [props.socket, tableStarted]);

  function playerJoined(dataSrc: string) {
    console.log("Joining table tile: " + dataSrc);
    const data = JSON.parse(dataSrc) as PlayerColorInfo;
    if (data.tableId !== tableInfo.tableId) {
      return;
    }
    setTableInfo((t) => {
      let newTable = { ...t };
      for (let p of newTable.playerColors) {
        if (p.color === data.color) {
          return newTable;
        }
      }
      newTable.playerColors.push({ color: data.color, userInfo: data.userInfo });
      if (
        data.userInfo?.username !== localStorage.getItem("user-name") &&
        newTable.playerColors.find((c) => c.userInfo?.username === localStorage.getItem("user-name"))
      ) {
        newUserAudio.play();
      }
      return newTable;
    });
  }

  function playerLeft(dataSrc: string) {
    console.log("Leaving table tile: " + dataSrc);
    const data = JSON.parse(dataSrc) as PlayerColorInfo;
    if (data.tableId !== tableInfo.tableId) {
      return;
    }
    setTableInfo((t) => {
      let newTable = { ...t };
      for (let p in newTable.playerColors) {
        if (newTable.playerColors[p].userInfo?.username === data.userInfo?.username) {
          newTable.playerColors.splice(+p, 1);
          return newTable;
        }
      }
      return newTable;
    });
  }

  function tableRemoved(tableId: string) {
    if (tableInfo.tableId === tableId) {
      setTableInfo((t) => Object.assign({}, t, { status: "notexist" }));
      if (params.tableId) {
        navigate("/projects/" + projectSlug);
      }
    }
  }

  function joinTable(event: React.MouseEvent, color: string) {
    setJoinLeaveProgressColor(color);
    event.preventDefault();
    fetch("/api/projects/" + projectSlug + "/join-table/" + tableInfo.tableId + "?color=" + color, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((result) => {
        console.log(result);
        setJoinLeaveProgressColor("");
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function leaveTable(event: React.MouseEvent, color: string) {
    setJoinLeaveProgressColor(color);
    event.preventDefault();
    fetch("/api/projects/" + projectSlug + "/leave-table/" + tableInfo.tableId, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((result) => {
        console.log(result);
        setJoinLeaveProgressColor("");
        setTableInfo((tableInfo) => {
          let newTableInfo = { ...tableInfo };
          for (let p in newTableInfo.playerColors) {
            let player = newTableInfo.playerColors[p];
            if (player.userInfo?.username === playerName) {
              newTableInfo.playerColors.splice(+p, 1);
              break;
            }
          }
          return newTableInfo;
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }

  function eraseTable(event: React.MouseEvent) {
    setDeleteProgressVisible(true);
    event.preventDefault();
    fetch("/api/projects/" + projectSlug + "/erase-table/" + tableInfo.tableId, {
      method: "GET",
    })
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        return Promise.reject(response);
      })
      .then((result) => {
        console.log(result);
        setDeleteProgressVisible(false);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  for (let optionName of sortedOptions) {
    let selectedValue = tableOptions[optionName];
    let displayValue = selectedValue.display_value;
    if (selectedValue.value === true) {
      displayValue = "✅";
    }
    if (selectedValue.value === false) {
      continue;
    }
    if (displayValue && (!props.options[optionName].hidden || props.showAllOptions)) {
      optionsDisplay.push(
        <Fragment key={"option-row-" + optionName}>
          <div className="table-tile-options-grid--key" key={"option-name-" + optionName}>
            {" "}
            {selectedValue.display_name}:
          </div>
          <div key={"option-value-" + selectedValue.value}> {displayValue}</div>
        </Fragment>,
      );
    }
  }

  let playerAmt = tableInfo.start_position?.options.numPlayers;
  let playerObjs = [];
  let playerInGame = "";
  let colorList = props.tableInfo.possibleColors ?? tableInfo.playerColors?.map((c) => c.color) ?? [];
  for (const color of colorList) {
    const colorObj: PlayerColorInfo = { color, player: "", badges: [] };
    if (tableInfo.playerColors) {
      let player = tableInfo.playerColors.find((c) => c.color === color);
      if (player) {
        colorObj.player = player.userInfo?.username;
        colorObj.badges = player.userInfo?.badges;
        colorObj.tableTileStats = player.tableTileStats;
        if (player.nonuser) {
          colorObj.player = "Dummy-" + color;
        }
        if (colorObj.player === playerName) {
          colorObj.active = true;
          playerInGame = color;
        } else {
          colorObj.active = false;
        }
      } else {
        colorObj.player = undefined;
        colorObj.active = false;
      }
    } else {
      colorObj.player = undefined;
      colorObj.active = false;
    }
    playerObjs.push(colorObj);
  }

  if (tableInfo.status !== "open") {
    playerObjs = playerObjs.filter((p) => p.player);
  }
  const playerDivs = playerObjs.map((p) => (
    <div className="player-div" key={"player-" + p.color}>
      {p.player ? (
        <img className="player-icon" src={`/assets/icons/user-${p.color}.png`} alt={`${p.color} player icon`} />
      ) : (
        <img className="player-icon" src={"/assets/icons/user.png"} alt="new player icon" />
      )}
      {joinLeaveProgressColor == p.color ? <CircularProgress className="table-tile-join-leave-progress" /> : null}
      {p.player ? (
        p.active && tableInfo.status === "open" ? (
          <button className={`player-button player-button--leave`} onClick={(e) => leaveTable(e, p.color)}>
            Leave table
          </button>
        ) : (
          <div className={`player-connected player-connected--${p.color}`}>
            <UsernameDisplay username={p.player} color={getCgoColor(p.color)}></UsernameDisplay>

            {(p.tableTileStats ?? []).map((stat) => {
              return (
                <div key={stat.name}>
                  {stat.displayName ?? stat.name}: {stat.numValue ?? stat.strValue}
                </div>
              );
            })}
          </div>
        )
      ) : playerInGame ? (
        <div className={`player-connected player-connected--${p.color}`}>Free to join</div>
      ) : (
        <button className={`player-button player-button--${p.color}`} onClick={(e) => joinTable(e, p.color)}>
          Join as {p.color}
        </button>
      )}
    </div>
  ));

  let eraseTableButton = null;
  if (
    (tableInfo.status !== "ended" && tableInfo.creator?.username === playerName) ||
    localStorage.getItem("user-role") === "superadmin"
  ) {
    eraseTableButton = (
      <button className="table-tile-erase-button" onClick={eraseTable}>
        <Delete />
      </button>
    );
  }

  let tableLobbyButton = null;
  if (tableInfo.status === "open") {
    tableLobbyButton = (
      <button className="table-tile-lobby-button" onClick={() => navigate(tableUrl)}>
        Go to lobby
      </button>
    );
  }
  if (params.tableId) {
    tableLobbyButton = (
      <button
        className="table-tile-lobby-button"
        onClick={() => navigate("/projects/" + projectSlug + "?openSection=join")}
      >
        Leave lobby
      </button>
    );
  }

  let tableStatus = "";
  let statusClass = "";
  let tableButtonText = "";
  let tableButtonAction = (e: React.MouseEvent) => {};
  if (tableInfo.status === "open") {
    if (tableInfo.creator?.username === playerName) {
      tableButtonAction = async (e) => {
        try {
          await startTable(
            e,
            tableInfo,
            props.decks ?? {},
            defaultOptions,
            projectSlug ?? "",
            components,
            props.dataSheet ?? {},
          );
          navigate(tableUrl);
        } catch (error) {
          if (error instanceof Error) {
            setErrorMessage(error.message);
            console.log(error);
          }
        }
      };
      tableButtonText = "Start this game";
    } else {
      tableStatus = `${tableInfo.creator?.username} may start the game.`;
      statusClass = "table-display__status--down";
    }
  } else if (tableInfo.status === "ended") {
    tableStatus = "This game has already ended. Go to the table to view the replay and results.";
    tableButtonText = "Go to replay";
    tableButtonAction = () => {
      navigate(tableUrl);
    };
  } else if (tableInfo.status === "midgame") {
    const action = tableInfo.playerColors?.filter((p) => p.userInfo?.username === playerName).length
      ? "play"
      : "spectate";
    tableStatus = `This game is currently running. Go to the table to ${action}.`;
    tableButtonText = action === "play" ? "Continue playing" : "View game";
    tableButtonAction = () => {
      navigate(tableUrl);
    };
  }

  function createTableLinkAndAddToClipboard() {
    var url = window.location.origin + "/projects/" + projectSlug + "/table/" + tableInfo.tableId;
    navigator.clipboard.writeText(url);
    setAlertVisible(true);
  }

  return (
    <div
      className={"table-tile" + (tableInfo.newCreated ? " table-new" : "")}
      id={"table-" + tableInfo.tableId}
      data-project={projectSlug}
    >
      <header className="table-tile-header">
        <div className="table-tile-game-title">{tableInfo.projectInfo?.project_name || props.projectName}</div>
        <div className="table-tile-table-id-link">
          <span className="table-tile-table-id">ID: {tableInfo.tableId}</span>
          <Tooltip title="Link to table" arrow placement="right">
            <button className="table-tile-link-button" onClick={createTableLinkAndAddToClipboard}>
              <Link fontSize="small" />
            </button>
          </Tooltip>
          <div className="table-link-clipboard-alert-container">
            <Fade
              in={alertVisible}
              timeout={{ enter: 400, exit: 600 }}
              addEndListener={() => {
                setTimeout(() => {
                  setAlertVisible(false);
                }, 1000);
              }}
            >
              <Alert severity="info" variant="standard">
                Copied to clipboard
              </Alert>
            </Fade>
          </div>
        </div>
        {tableInfo.isPrivate ? <div className="table-tile-table-private">Private table</div> : <></>}

        <div className="table-tile-created-time">
          Created {new Date(+tableInfo.createdTime).toLocaleString()} by{" "}
          {tableInfo.creator && (
            <UsernameDisplay username={tableInfo.creator.username} badges={tableInfo.creator.badges} />
          )}
        </div>
        <div className="table-tile-players-actions">
          {playerAmt ? <div className="num-players">{playerAmt} player game</div> : ""}
          <div className="actions-played">{Math.max(0, tableInfo.expected_move_id - 1)} turns played </div>
        </div>
      </header>

      <div className="table-tile-options-grid">
        <div className="table-tile-options-grid--key" key={"table-mode"}>
          Table mode:
        </div>
        <div>{tableInfo.tableMode}</div>
        <div className="table-tile-options-grid--key" key={"table-status"}>
          Table status:
        </div>
        <div>{tableInfo.status}</div>
        {optionsDisplay}
      </div>

      <div className="table-tile-spacer" />
      {props.activePlayer ? <div className="active-player">Active player: {props.activePlayer}</div> : <></>}
      {playerObjs.length > 0 ? (
        <div className="table-tile-player-list">
          <div className="table-tile-player-list-header">Players:</div>
          <div className="table-tile-player-list-grid">{playerDivs}</div>
        </div>
      ) : (
        <div className="table-tile-player-list-header">{tableInfo.numPlayers || "No"} users connected. </div>
      )}
      {eraseTableButton}
      {deleteProgressVisible ? <CircularProgress className="table-tile-delete-progress" /> : null}
      {tableStatus && <div className={`table-tile-status ${statusClass}`}>{tableStatus}</div>}
      <div className="table-tile-action-buttons-container">
        {tableLobbyButton}
        {tableButtonText && (
          <button className="table-tile-action-button" onClick={tableButtonAction}>
            {tableButtonText}
          </button>
        )}
      </div>
      {errorMessage && <div className="table-tile-error-message">{errorMessage}</div>}
    </div>
  );
}

export default TableTile;
